import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useProjectConfig, useUpdateProjectConfig } from '../../../api/hooks/useProjectConfig';
import { useProjectInitialization } from '../../../api/hooks/useProjectInitialization';
import { useAutosave } from '../../../hooks/useAutosave';
import { PmCard } from '../../../components/pm';
import { AutosaveIndicator } from '../../../components/common/AutosaveIndicator';
import { useNotifications } from '../../../context/NotificationContext';

/**
 * Projekt-Charakterisierung: Vertragstyp, Magisches Dreieck, Stacey-Modell.
 * Gemeinsam genutzt von Projektauftrag und Ziele (PHQ-78) — gleiche Daten
 * (project_config + project_initialization), daher überall synchron.
 */

export type Axis = 'scope' | 'cost' | 'schedule';
export type StaceyZone = 'simple' | 'complicated' | 'complex' | 'chaotic';

export const ALL_AXES: Axis[] = ['scope', 'cost', 'schedule'];
export const MAX_FIXED = 2;

/**
 * Stacey 4x4:
 *   x = Sicherheit über Vorgehen — 0 = hoch (links), 3 = gering (rechts)
 *   y = Index von OBEN nach UNTEN — 0 = oben (gering Verständnis), 3 = unten (hoch Verständnis)
 *
 * Konsequenz: simple steht unten-links, chaotic oben-rechts (gamifizierter
 * "schwerer-rechts-oben"-Look statt klassisch invertierter Stacey-Darstellung).
 */
export const ZONE_GRID: StaceyZone[][] = [
  // y=0 — oben (Verständnis gering)
  ['complex',     'complex',     'chaotic',     'chaotic'],
  // y=1
  ['complicated', 'complex',     'complex',     'chaotic'],
  // y=2
  ['complicated', 'complicated', 'complex',     'complex'],
  // y=3 — unten (Verständnis hoch)
  ['simple',      'complicated', 'complicated', 'complex'],
];

const ZONE_COLORS: Record<StaceyZone, { bg: string; selBg: string; fg: string }> = {
  simple:      { bg: 'rgba(52,211,153,0.18)', selBg: '#16a34a', fg: '#16a34a' },
  complicated: { bg: 'rgba(251,191,36,0.18)', selBg: '#ca8a04', fg: '#ca8a04' },
  complex:     { bg: 'rgba(255,154,86,0.20)', selBg: '#ea580c', fg: '#ea580c' },
  chaotic:     { bg: 'rgba(220,38,38,0.20)',  selBg: '#dc2626', fg: '#dc2626' },
};

export function clampIdx(n: unknown, fallback: number): number {
  const v = typeof n === 'number' ? n : Number(n);
  if (!Number.isFinite(v)) return fallback;
  return Math.max(0, Math.min(3, Math.round(v)));
}

// ── Stacey: freier Punkt (PHQ-77) ────────────────────────────────────────────

/** Freie Lage im 4x4-Diagramm in Zellen-Einheiten (0..4), py von OBEN nach UNTEN. */
export interface StaceyPoint { px: number; py: number }

const clampPt = (n: number) => Math.max(0, Math.min(4, n));

/** Zelle (0..3), in der der Punkt liegt — daraus ergibt sich die Kategorie. */
export function cellOf(p: StaceyPoint): { x: number; y: number } {
  return { x: Math.min(3, Math.floor(clampPt(p.px))), y: Math.min(3, Math.floor(clampPt(p.py))) };
}

export function centerOf(x: number, y: number): StaceyPoint {
  return { px: x + 0.5, py: y + 0.5 };
}

/**
 * Server-Wert -> Lage. Das Backend speichert die Lage als Dezimalzahl 0..4
 * (Skala = Zellen-Einheiten, Zelle n = Bereich n..n+1). Ganzzahlen 0..3 sind
 * Altwerte aus der Zeit, als nur die Zelle gespeichert wurde: sie erscheinen in
 * der Zellenmitte (n + 0,5). Neue Lagen werden nie exakt ganzzahlig gespeichert
 * (siehe staceyToConfig), die Deutung ist daher eindeutig.
 */
export function staceyCoordFromConfig(v: unknown, fallbackCell: number): number {
  const n = typeof v === 'number' ? v : Number(v);
  if (v === null || v === undefined || !Number.isFinite(n)) return fallbackCell + 0.5;
  const c = clampPt(n);
  return Number.isInteger(c) && c < 4 ? c + 0.5 : c;
}

export function staceyPointFromConfig(x: unknown, y: unknown): StaceyPoint {
  return { px: staceyCoordFromConfig(x, 0), py: staceyCoordFromConfig(y, 3) };
}

/** Lage -> Server-Werte; exakte Ganzzahlen werden minimal verschoben, damit sie nicht als Altwert gelten. */
export function staceyToConfig(p: StaceyPoint): { stacey_x: number; stacey_y: number } {
  const enc = (n: number) => {
    const c = Math.round(clampPt(n) * 1000) / 1000;
    if (!Number.isInteger(c)) return c;
    return c >= 4 ? 3.999 : c + 0.001;
  };
  return { stacey_x: enc(p.px), stacey_y: enc(p.py) };
}

// ── Magisches Dreieck ────────────────────────────────────────────────────────

interface Pos { x: number; y: number; }

// SVG-Viewbox: 360x320. Kreise mit Radius 44 + Glow-Halo bis 56 — viewBox großzügig
// dimensioniert, damit nichts abgeschnitten wird.
const VIEW_W = 360;
const VIEW_H = 320;

// Default-Layout (0 fix): scope oben, cost+schedule unten.
const DEFAULT_POS: Record<Axis, Pos> = {
  scope:    { x: 180, y: 70  },
  cost:     { x: 70,  y: 270 },
  schedule: { x: 290, y: 270 },
};

// Slot-Positionen, wenn 2 Achsen fix sind: beide fix oben, flex unten Mitte.
const SLOT_TOP_LEFT:     Pos = { x: 90,  y: 80  };
const SLOT_TOP_RIGHT:    Pos = { x: 270, y: 80  };
const SLOT_BOTTOM_CENTER: Pos = { x: 180, y: 270 };
// Eine fixe Achse: Spitze oben, die beiden freien unten links/rechts.
const SLOT_APEX:         Pos = { x: 180, y: 70  };
const SLOT_BOTTOM_LEFT:  Pos = { x: 70,  y: 270 };
const SLOT_BOTTOM_RIGHT: Pos = { x: 290, y: 270 };

/**
 * Berechnet die aktuellen Positionen aller 3 Achsen.
 * - 1 fix: die fixierte Achse sitzt an der Spitze (PHQ-76), die anderen unten
 * - 2 fix: fixierte oben (links + rechts), flex unten zentral
 * - 0 fix: Default (scope oben, cost/schedule unten)
 *
 * Reihenfolge innerhalb einer Gruppe: nach ALL_AXES-Ordnung (scope < cost < schedule),
 * damit das Layout deterministisch ist — egal in welcher Reihenfolge der User klickt.
 */
export function computePositions(fixed: Axis[]): Record<Axis, Pos> {
  const sortedFixed = [...fixed].sort((a, b) => ALL_AXES.indexOf(a) - ALL_AXES.indexOf(b));
  const rest = ALL_AXES.filter((a) => !fixed.includes(a));
  if (fixed.length === 1) {
    return {
      [sortedFixed[0]!]: SLOT_APEX,
      [rest[0]!]: SLOT_BOTTOM_LEFT,
      [rest[1]!]: SLOT_BOTTOM_RIGHT,
    } as Record<Axis, Pos>;
  }
  if (fixed.length === 2) {
    return {
      [sortedFixed[0]!]: SLOT_TOP_LEFT,
      [sortedFixed[1]!]: SLOT_TOP_RIGHT,
      [rest[0]!]: SLOT_BOTTOM_CENTER,
    } as Record<Axis, Pos>;
  }
  return DEFAULT_POS;
}

const AXIS_LABEL_DE: Record<Axis, string> = {
  scope: 'Scope',
  cost: 'Kosten',
  schedule: 'Zeit',
};

export function MagicTriangleCard({ fixed, onToggle }: { fixed: Axis[]; onToggle: (axis: Axis) => void }) {
  const { t } = useTranslation('projects');
  const flex = useMemo(() => ALL_AXES.filter((a) => !fixed.includes(a)), [fixed]);
  const positions = useMemo(() => computePositions(fixed), [fixed]);

  return (
    <PmCard>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {t('projektauftrag.magicTriangle', 'Magisches Dreieck')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('projektauftrag.magicTriangleHint',
          'Maximal 2 von 3 Achsen können gleichzeitig fix sein. Klicke auf eine Ecke, um sie zu fixieren oder freizugeben. Die freie Variable wird zur Stellschraube.')}
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
        <Box
          component="svg"
          data-testid="magic-triangle-svg"
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          sx={{
            width: '100%',
            maxWidth: 300,
            height: 'auto',
            overflow: 'visible',
          }}
        >
          <defs>
            <radialGradient id="mt-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="rgba(13,148,136,0.18)" />
              <stop offset="100%" stopColor="rgba(13,148,136,0)" />
            </radialGradient>
            <filter id="mt-pulse" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
          </defs>

          <polygon
            points={ALL_AXES.map((a) => `${positions[a].x},${positions[a].y}`).join(' ')}
            fill="url(#mt-glow)"
            stroke="currentColor"
            strokeWidth={1.5}
            opacity={0.45}
            style={{ transition: 'all 0.45s cubic-bezier(0.34, 1.56, 0.64, 1)' }}
          />

          {ALL_AXES.map((axis) => {
            const p = positions[axis];
            const isFixed = fixed.includes(axis);
            const TRANSITION = 'all 0.45s cubic-bezier(0.34, 1.56, 0.64, 1)';
            return (
              <g
                key={axis}
                data-testid={`triangle-axis-${axis}`}
                data-fixed={isFixed ? 'true' : 'false'}
                data-x={p.x}
                data-y={p.y}
                onClick={() => onToggle(axis)}
                style={{ cursor: 'pointer' }}
              >
                {isFixed && (
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r={56}
                    fill="rgba(13,148,136,0.20)"
                    style={{ animation: 'mt-pulse 2s ease-in-out infinite', transition: TRANSITION }}
                  />
                )}
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={44}
                  fill={isFixed ? 'var(--rittec-petrol)' : 'rgba(127,127,127,0.15)'}
                  stroke={isFixed ? 'var(--pm-petrol-700)' : 'var(--pm-fg-subtle)'}
                  strokeWidth={isFixed ? 3 : 1.5}
                  style={{ transition: TRANSITION }}
                />
                <text
                  x={p.x}
                  y={p.y - 4}
                  textAnchor="middle"
                  fontSize={15}
                  fontWeight={700}
                  fill={isFixed ? '#fff' : 'currentColor'}
                  style={{ pointerEvents: 'none', transition: TRANSITION }}
                >
                  {AXIS_LABEL_DE[axis]}
                </text>
                <text
                  x={p.x}
                  y={p.y + 16}
                  textAnchor="middle"
                  fontSize={11}
                  fontWeight={600}
                  fill={isFixed ? '#fff' : 'currentColor'}
                  opacity={isFixed ? 0.95 : 0.7}
                  style={{ pointerEvents: 'none', transition: TRANSITION }}
                >
                  {isFixed ? '⚓ FIX' : '~ flex'}
                </text>
              </g>
            );
          })}
        </Box>
      </Box>

      <style>{`
        @keyframes mt-pulse {
          0%, 100% { opacity: 0.5; transform: scale(1); transform-origin: center; }
          50% { opacity: 0.8; transform: scale(1.08); transform-origin: center; }
        }
      `}</style>

      <Stack direction="row" spacing={2} sx={{ mt: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Typography variant="body2">
          {/* Der Schlüssel erwartet `count` — ohne ihn stand „{{count}} fixierte Achsen" wörtlich da. */}
          <strong>{t('projektauftrag.fixedCount', { count: fixed.length, defaultValue: '{{count}} fixierte Achsen' })} / {MAX_FIXED}</strong>
          {fixed.length > 0 && (
            <span style={{ marginLeft: 8, color: 'var(--pm-petrol-700)' }}>
              ({fixed.map((a) => AXIS_LABEL_DE[a]).join(' + ')})
            </span>
          )}
        </Typography>
        {flex.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {t('projektauftrag.stellschraube', 'Stellschraube')}: <strong>{flex.map((a) => AXIS_LABEL_DE[a]).join(', ')}</strong>
          </Typography>
        )}
      </Stack>
    </PmCard>
  );
}

// ── Stacey-Karte ─────────────────────────────────────────────────────────────

export function StaceyCard({ point, onChange }: { point: StaceyPoint; onChange: (p: StaceyPoint) => void }) {
  const { t } = useTranslation('projects');
  const gridRef = useRef<HTMLDivElement | null>(null);
  const dragging = useRef(false);

  const cell = cellOf(point);
  const activeZone: StaceyZone = ZONE_GRID[cell.y]![cell.x]!;
  const activeColors = ZONE_COLORS[activeZone];

  /** Lage aus Mauszeiger-Koordinaten; ohne Layout (z. B. jsdom) Mitte der Ersatzzelle. */
  const pointFromEvent = (clientX: number, clientY: number, fallbackCell?: { x: number; y: number }): StaceyPoint | null => {
    const rect = gridRef.current?.getBoundingClientRect();
    if (rect && rect.width > 0 && rect.height > 0) {
      return {
        px: clampPt(((clientX - rect.left) / rect.width) * 4),
        py: clampPt(((clientY - rect.top) / rect.height) * 4),
      };
    }
    return fallbackCell ? centerOf(fallbackCell.x, fallbackCell.y) : null;
  };

  return (
    <PmCard>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {t('projektauftrag.stacey', 'Stacey-Modell')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('projektauftrag.staceyHint',
          'Setze den Punkt frei im Diagramm (Klick oder ziehen). Die Kategorie ergibt sich aus der Lage; Grün (unten links) = Wasserfall, Rot (oben rechts) = Krisenmodus.')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
          <Box
            sx={{
              display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
              mr: 1.5, py: 1.5, alignItems: 'flex-end',
              fontSize: '0.68rem', color: 'text.secondary', fontWeight: 500,
            }}
          >
            <span>gering</span>
            <Box sx={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', my: 1, fontWeight: 600 }}>
              Verständnis Anforderungen
            </Box>
            <span>hoch</span>
          </Box>

          <Box>
            <Box
              ref={gridRef}
              data-testid="stacey-grid"
              onPointerDown={(e) => {
                dragging.current = true;
                const p = pointFromEvent(e.clientX, e.clientY);
                if (p) onChange(p);
              }}
              onPointerMove={(e) => {
                if (!dragging.current) return;
                const p = pointFromEvent(e.clientX, e.clientY);
                if (p) onChange(p);
              }}
              onPointerUp={() => { dragging.current = false; }}
              onPointerLeave={() => { dragging.current = false; }}
              sx={{
                position: 'relative',
                display: 'grid',
                // PHQ-56: Bei 52px lief „Complicated" aus der Zelle. 72px tragen
                // die längste Zonenbezeichnung in einer Zeile; der Umbruch unten
                // fängt längere Übersetzungen ab, statt sie überlaufen zu lassen.
                gridTemplateColumns: 'repeat(4, 72px)',
                gridTemplateRows: 'repeat(4, 56px)',
                gap: '2px',
                borderRadius: 2,
                bgcolor: 'var(--pm-bg-deep)',
                cursor: 'crosshair',
                touchAction: 'none',
                userSelect: 'none',
              }}
            >
              {ZONE_GRID.flatMap((row, y) =>
                row.map((zone, x) => {
                  const isActive = cell.x === x && cell.y === y;
                  return (
                    <Box
                      key={`${x}-${y}`}
                      data-testid={`stacey-cell-${x}-${y}`}
                      data-active={isActive ? 'true' : 'false'}
                      onClick={(e) => {
                        const p = pointFromEvent(e.clientX, e.clientY, { x, y });
                        if (p) onChange(p);
                      }}
                      sx={{
                        // Kategorien sind der ausgegraute Hintergrund; nur die Zelle,
                        // in der der Punkt liegt, trägt die Zonenfarbe.
                        borderRadius: 1.5,
                        bgcolor: isActive ? ZONE_COLORS[zone].bg : 'rgba(127,127,127,0.10)',
                        border: '1px solid',
                        borderColor: isActive ? ZONE_COLORS[zone].fg : 'rgba(127,127,127,0.25)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.62rem',
                        fontWeight: isActive ? 700 : 500,
                        color: isActive ? 'text.primary' : 'text.disabled',
                        textTransform: 'capitalize',
                        textAlign: 'center',
                        lineHeight: 1.1,
                        px: 0.5,
                        overflow: 'hidden',
                        overflowWrap: 'anywhere',
                        hyphens: 'auto',
                      }}
                    >
                      {t(`projektauftrag.zones.${zone}`, zone)}
                    </Box>
                  );
                }),
              )}
              <Box
                data-testid="stacey-point"
                data-px={point.px.toFixed(2)}
                data-py={point.py.toFixed(2)}
                sx={{
                  position: 'absolute',
                  left: `${(point.px / 4) * 100}%`,
                  top: `${(point.py / 4) * 100}%`,
                  width: 18,
                  height: 18,
                  ml: '-9px',
                  mt: '-9px',
                  borderRadius: '50%',
                  bgcolor: activeColors.selBg,
                  border: '3px solid #fff',
                  boxShadow: `0 2px 10px ${activeColors.fg}88`,
                  pointerEvents: 'none',
                }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex', justifyContent: 'space-between',
                mt: 1, px: 0.5, fontSize: '0.68rem', color: 'text.secondary', fontWeight: 500,
              }}
            >
              <span>hoch</span>
              <span>gering</span>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5, textAlign: 'center', fontWeight: 600 }}
            >
              Sicherheit über Vorgehen
            </Typography>
          </Box>
        </Box>

        <Alert severity="info" data-testid="stacey-zone-info">
          <Typography variant="subtitle2" fontWeight={700} sx={{ textTransform: 'capitalize' }}>
            {t(`projektauftrag.zones.${activeZone}`, activeZone)}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {t(`projektauftrag.zoneDesc.${activeZone}`,
              activeZone === 'simple'
                ? 'Klassisches Vorgehen, Plan-Driven, Wasserfall.'
                : activeZone === 'complicated'
                  ? 'Experten-getrieben, klassisches PM mit Lenkungskreis.'
                  : activeZone === 'complex'
                    ? 'Agil – Scrum, Kanban, kurze Lernzyklen, MVP-Ansatz.'
                    : 'Krisenmodus – Stabilisieren, dann strukturieren.')}
          </Typography>
        </Alert>
      </Box>
    </PmCard>
  );
}

// ── Vertragstyp ──────────────────────────────────────────────────────────────

export function VertragCard({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation('projects');
  return (
    <PmCard>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {t('projektauftrag.vertrag', 'Vertragsbedingungen / Auftragstyp')}
      </Typography>
      <TextField
        placeholder="z.B. Festpreis, Zeit & Material, agiler Rahmenvertrag, Internes Projekt"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        multiline
        rows={2}
        fullWidth
      />
    </PmCard>
  );
}

// ── Eigenständiger Block für die Ziele-Seite (PHQ-78) ────────────────────────

interface CharakterValues { fixed: Axis[]; point: StaceyPoint; vertrag: string }

/**
 * Vertragstyp + Dreieck + Stacey mit eigenem Autosave — für Seiten, die diese
 * Daten außerhalb des Projektauftrags zeigen. Speichert dieselben Felder
 * (project_config bzw. projektauftrag_vertrag) wie der Projektauftrag.
 */
export function ProjektCharakterSections({ projectId }: { projectId: string }) {
  const { t } = useTranslation('projects');
  const { notify } = useNotifications();
  const { data, isLoading } = useProjectConfig(projectId);
  const updateMut = useUpdateProjectConfig(projectId);
  const init = useProjectInitialization(projectId);
  const config = ((data as unknown as { data?: { magic_triangle_fixed: Axis[]; stacey_x: number; stacey_y: number } } | undefined)?.data ?? null);

  const [fixed, setFixed] = useState<Axis[]>([]);
  const [point, setPoint] = useState<StaceyPoint>(centerOf(0, 3));
  const [vertrag, setVertrag] = useState('');

  const autosave = useAutosave<CharakterValues>({
    onSave: async (v) => {
      await updateMut.mutateAsync({
        magic_triangle_fixed: v.fixed,
        magic_triangle_flex: ALL_AXES.filter((a) => !v.fixed.includes(a)),
        ...staceyToConfig(v.point),
      });
      await init.save({ projektauftrag_vertrag: v.vertrag.trim() });
    },
  });

  const fixedKey = config?.magic_triangle_fixed?.join(',') ?? '';
  const cfgX = config?.stacey_x ?? null;
  const cfgY = config?.stacey_y ?? null;
  useEffect(() => {
    if (autosave.isDirty || !config) return;
    setFixed((config.magic_triangle_fixed ?? []).filter((a): a is Axis => ALL_AXES.includes(a)).slice(0, MAX_FIXED));
    setPoint(staceyPointFromConfig(config.stacey_x, config.stacey_y));
  }, [autosave.isDirty, fixedKey, cfgX, cfgY]);

  useEffect(() => {
    if (autosave.isDirty) return;
    setVertrag(init.data.projektauftrag_vertrag ?? '');
  }, [autosave.isDirty, init.data.projektauftrag_vertrag]);

  const push = (next: Partial<{ fixed: Axis[]; point: StaceyPoint; vertrag: string }>) => {
    const f = next.fixed ?? fixed;
    const p = next.point ?? point;
    autosave.schedule({ fixed: f, point: p, vertrag: next.vertrag ?? vertrag });
  };

  const toggleAxis = (axis: Axis) => {
    let next: Axis[];
    if (fixed.includes(axis)) {
      next = fixed.filter((a) => a !== axis);
    } else {
      if (fixed.length >= MAX_FIXED) {
        notify(t('projektauftrag.errorMaxFixed', 'Maximal 2 Achsen können gleichzeitig fix sein.'), 'warning');
        return;
      }
      next = [...fixed, axis];
    }
    setFixed(next);
    push({ fixed: next });
  };

  const changePoint = (p: StaceyPoint) => {
    setPoint(p);
    push({ point: p });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Stack spacing={3} sx={{ mt: 3 }} data-testid="ziele-charakter">
      <Stack direction="row" justifyContent="flex-end">
        <AutosaveIndicator status={autosave.status} onRetry={autosave.flush} />
      </Stack>
      <VertragCard value={vertrag} onChange={(v) => { setVertrag(v); push({ vertrag: v }); }} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, alignItems: 'start' }}>
        <MagicTriangleCard fixed={fixed} onToggle={toggleAxis} />
        <StaceyCard point={point} onChange={changePoint} />
      </Box>
    </Stack>
  );
}
